import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Play, RotateCcw, UserX, Volume2, Settings, 
  Users, CheckCircle, XCircle, Clock, RefreshCw,
  ExternalLink, Monitor, Printer, LayoutGrid, Building2, User, Check,
  ArrowRight, Clipboard, Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Switch } from "@/components/ui/switch";
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TurnosPage = () => {
  const navigate = useNavigate();
  const [estado, setEstado] = useState(null);
  const [turnos, setTurnos] = useState({ turnos: [], stats: {} });
  const [turnosEnRecepcion, setTurnosEnRecepcion] = useState([]);
  const [config, setConfig] = useState(null);
  const [puntosAtencion, setPuntosAtencion] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [boxSelectDialogOpen, setBoxSelectDialogOpen] = useState(false);
  const [selectedTurnoForBox, setSelectedTurnoForBox] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [estadoRes, turnosRes, configRes, puntosRes, recepcionRes] = await Promise.all([
        axios.get(`${API}/turnos/estado`, { withCredentials: true }),
        axios.get(`${API}/turnos/lista`, { withCredentials: true }),
        axios.get(`${API}/turnos/config`, { withCredentials: true }),
        axios.get(`${API}/puntos-atencion?activos_only=false`, { withCredentials: true }),
        axios.get(`${API}/turnos/en-recepcion`, { withCredentials: true })
      ]);
      setEstado(estadoRes.data);
      setTurnos(turnosRes.data);
      setConfig(configRes.data);
      setPuntosAtencion(puntosRes.data);
      setTurnosEnRecepcion(recepcionRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // WebSocket for real-time updates
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL
      ? process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/turnos'
      : `ws://${window.location.host}/api/ws/turnos`;
    
    let ws = null;
    let reconnectTimeout = null;
    
    const connect = () => {
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket turnos connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('WS turnos message:', message.type);
          
          if (message.type === 'TURNO_LLAMADO' || message.type === 'TURNO_NUEVO' || 
              message.type === 'TURNO_RECEPCION' || message.type === 'ESTADO_UPDATE' || 
              message.type === 'BOX_UPDATE' || message.type === 'TURNOS_REINICIADOS') {
            fetchData();
          }
        } catch (e) {
          console.error('WS parse error:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket turnos disconnected, reconnecting...');
        reconnectTimeout = setTimeout(connect, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket turnos error:', error);
      };
    };
    
    connect();
    
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // STEP 1: Call to reception
  const llamarRecepcion = async (tipoTurno) => {
    setCalling(true);
    try {
      const response = await axios.post(`${API}/turnos/llamar-recepcion?tipo_turno=${tipoTurno}`, {}, {
        withCredentials: true
      });
      if (response.data.turno) {
        toast.success(
          `Turno ${response.data.turno.numero_formateado} llamado a Recepción`,
          { duration: 4000 }
        );
      } else {
        toast.info(response.data.message || 'No hay turnos en espera');
      }
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al llamar turno');
    } finally {
      setCalling(false);
    }
  };

  // STEP 2: Call from reception to box
  const llamarBox = async (turnoId, boxNumero = null) => {
    setCalling(true);
    try {
      const url = boxNumero 
        ? `${API}/turnos/llamar-box?turno_id=${turnoId}&box_numero=${boxNumero}`
        : `${API}/turnos/llamar-box?turno_id=${turnoId}`;
      
      const response = await axios.post(url, {}, { withCredentials: true });
      
      if (response.data.turno) {
        const boxInfo = response.data.box_asignado;
        toast.success(
          `Turno ${response.data.turno.numero_formateado} enviado a ${boxInfo?.nombre || 'Box'}`,
          { duration: 4000 }
        );
      } else {
        toast.info(response.data.message || 'No se pudo asignar el turno');
      }
      setBoxSelectDialogOpen(false);
      setSelectedTurnoForBox(null);
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al llamar al box');
    } finally {
      setCalling(false);
    }
  };

  // Open box selection dialog
  const openBoxSelect = (turno) => {
    setSelectedTurnoForBox(turno);
    setBoxSelectDialogOpen(true);
  };

  const rellamarTurno = async (boxNumero) => {
    try {
      const response = await axios.post(`${API}/turnos/rellamar-auto?box_numero=${boxNumero}`, {}, {
        withCredentials: true
      });
      if (response.data.turno) {
        toast.success(`Rellamando turno ${response.data.turno.numero_formateado}`);
      } else {
        toast.info('No hay turno actual para rellamar');
      }
    } catch (error) {
      toast.error('Error al rellamar turno');
    }
  };

  const marcarNoShow = async (boxNumero) => {
    try {
      await axios.post(
        `${API}/turnos/finalizar-atencion?box_numero=${boxNumero}`, 
        {}, 
        { withCredentials: true }
      );
      toast.info('Turno marcado como ausente');
      fetchData();
    } catch (error) {
      toast.error('Error al marcar ausente');
    }
  };

  const finalizarAtencion = async (boxNumero) => {
    try {
      await axios.post(`${API}/turnos/finalizar-atencion?box_numero=${boxNumero}`, {}, {
        withCredentials: true
      });
      toast.success('Atención finalizada');
      fetchData();
    } catch (error) {
      toast.error('Error al finalizar atención');
    }
  };

  const reiniciarTurnos = async () => {
    try {
      await axios.post(`${API}/turnos/reiniciar`, {}, { withCredentials: true });
      toast.success('Turnos reiniciados');
      setResetDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error al reiniciar turnos');
    }
  };

  const updateConfig = async (newConfig) => {
    try {
      await axios.put(`${API}/turnos/config`, newConfig, { withCredentials: true });
      toast.success('Configuración guardada');
      fetchData();
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const puntosActivos = puntosAtencion.filter(p => p.activo);
  const stats = turnos.stats || {};
  
  // Get current turns by box
  const turnosPorBox = {};
  puntosAtencion.forEach(punto => {
    const turnoEnBox = estado?.turnos_por_punto?.[punto.numero]?.turno;
    turnosPorBox[punto.numero] = turnoEnBox;
  });

  // Count waiting turns by type
  const turnosEsperando = turnos.turnos?.filter(t => t.status === 'ESPERANDO') || [];
  const turnosGeneralEsperando = turnosEsperando.filter(t => t.tipo_turno !== 'EMPRESA').length;
  const turnosEmpresaEsperando = turnosEsperando.filter(t => t.tipo_turno === 'EMPRESA').length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="turnos-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Control de Turnos</h1>
            <p className="text-slate-500 mt-1">
              Flujo: Espera → Recepción → Box
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => window.open('/display', '_blank')}
              className="gap-2"
              data-testid="btn-ver-pantalla"
            >
              <Monitor className="w-4 h-4" />
              Ver Pantalla
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('/kiosk', '_blank')}
              className="gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              Kiosco
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/box-config')}
              className="gap-2"
            >
              <LayoutGrid className="w-4 h-4" />
              Boxes
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfigDialogOpen(true)}
              className="gap-2"
            >
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* STEP 1: Call to Reception */}
        <Card className="border-2 border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <Clipboard className="w-5 h-5" />
              Paso 1: Llamar a Recepción
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={() => llamarRecepcion('GENERAL')}
                disabled={calling || turnosGeneralEsperando === 0}
                className="h-20 flex flex-col gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="btn-recepcion-general"
              >
                <User className="w-8 h-8" />
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Llamar General a Recepción</span>
                  <span className="text-sm opacity-80">{turnosGeneralEsperando} en espera</span>
                </div>
              </Button>
              
              <Button
                onClick={() => llamarRecepcion('EMPRESA')}
                disabled={calling || turnosEmpresaEsperando === 0}
                className="h-20 flex flex-col gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="btn-recepcion-empresa"
              >
                <Building2 className="w-8 h-8" />
                <div className="flex flex-col items-center">
                  <span className="font-semibold">Llamar Empresa a Recepción</span>
                  <span className="text-sm opacity-80">{turnosEmpresaEsperando} en espera</span>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* STEP 2: Turns in Reception - Send to Box */}
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <ArrowRight className="w-5 h-5" />
              Paso 2: En Recepción → Enviar a Box ({turnosEnRecepcion.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {turnosEnRecepcion.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Clipboard className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No hay pacientes en recepción</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {turnosEnRecepcion.map((turno) => (
                  <div
                    key={turno.id}
                    className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm space-y-3"
                    data-testid={`turno-recepcion-${turno.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold font-mono text-blue-700">
                        {turno.numero_formateado}
                      </span>
                      <Badge className={
                        turno.tipo_turno === 'EMPRESA'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-slate-100 text-slate-700'
                      }>
                        {turno.tipo_turno === 'EMPRESA' ? 'Empresa' : 'General'}
                      </Badge>
                    </div>

                    {/* RUT block */}
                    {turno.rut ? (
                      <div className="bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-[10px] uppercase text-slate-500 tracking-wide leading-tight">RUT</p>
                            <p className="text-sm font-mono font-semibold text-slate-900 truncate">{turno.rut}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                              navigator.clipboard.writeText(turno.rut);
                              toast.success(`RUT copiado: ${turno.rut}`);
                            }}
                            data-testid={`btn-copy-rut-${turno.id}`}
                          >
                            Copiar
                          </Button>
                        </div>
                        {turno.paciente_encontrado ? (
                          <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                            {turno.paciente_data?.nombre_completo || 'Paciente en BD'}
                          </p>
                        ) : (
                          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                            No registrado — completar datos
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Sin RUT (paciente tomó turno sin identificarse)</p>
                    )}

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        onClick={() => llamarBox(turno.id)}
                        disabled={calling}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Auto Box
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openBoxSelect(turno)}
                        disabled={calling}
                      >
                        Elegir Box
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Boxes Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" />
              Estado de Boxes ({puntosActivos.length} activos)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {puntosActivos.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <LayoutGrid className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay boxes activos</p>
                <Button 
                  variant="link" 
                  onClick={() => navigate('/box-config')}
                  className="mt-2"
                >
                  Configurar Boxes
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {puntosActivos.map((punto) => {
                  const turnoActual = turnosPorBox[punto.numero];
                  const esBoxEmpresa = punto.nombre.toLowerCase().includes('empresa');
                  
                  return (
                    <Card 
                      key={punto.id} 
                      className="overflow-hidden"
                      style={{ borderTopColor: punto.color, borderTopWidth: '4px' }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: punto.color }}
                            />
                            <span className="font-semibold text-slate-700">{punto.nombre}</span>
                          </div>
                          {esBoxEmpresa && (
                            <Badge variant="outline" className="text-purple-600 border-purple-200">
                              <Building2 className="w-3 h-3 mr-1" />
                              Emp
                            </Badge>
                          )}
                        </div>
                        
                        <div 
                          className="text-center py-4 rounded-lg mb-3"
                          style={{ backgroundColor: `${punto.color}15` }}
                        >
                          <p className="text-sm text-slate-500 mb-1">Turno Actual</p>
                          <p 
                            className="text-4xl font-bold"
                            style={{ color: punto.color }}
                          >
                            {turnoActual?.numero_formateado || '---'}
                          </p>
                          {turnoActual && (
                            <Badge className="mt-2 bg-blue-100 text-blue-700">
                              En atención
                            </Badge>
                          )}
                        </div>
                        
                        {turnoActual?.rut && (
                          <div className="bg-slate-50 rounded-md px-2 py-2 border border-slate-200 mb-3 text-xs">
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-mono font-semibold text-slate-900 truncate">{turnoActual.rut}</span>
                              <div className="flex gap-1 shrink-0">
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(turnoActual.rut);
                                    toast.success('RUT copiado');
                                  }}
                                  className="px-2 py-0.5 rounded bg-white border border-slate-200 hover:bg-slate-100 text-slate-700"
                                  data-testid={`btn-copy-box-rut-${punto.numero}`}
                                >
                                  Copiar
                                </button>
                                <button
                                  onClick={() => navigate(`/vaccinations/new?rut=${encodeURIComponent(turnoActual.rut)}`)}
                                  className="px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                                  data-testid={`btn-vacc-box-${punto.numero}`}
                                >
                                  Vacunar
                                </button>
                              </div>
                            </div>
                            {turnoActual.paciente_data?.nombre_completo && (
                              <p className="text-[10px] text-emerald-700 mt-1 truncate">
                                {turnoActual.paciente_data.nombre_completo}
                              </p>
                            )}
                          </div>
                        )}

                        {turnoActual && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-1"
                              onClick={() => rellamarTurno(punto.numero)}
                            >
                              <Volume2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                              onClick={() => marcarNoShow(punto.numero)}
                            >
                              <UserX className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              onClick={() => finalizarAtencion(punto.numero)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-slate-500" />
              <p className="text-xl font-bold">{stats.total || 0}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-amber-500" />
              <p className="text-xl font-bold">{stats.esperando || 0}</p>
              <p className="text-xs text-slate-500">Esperando</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Clipboard className="w-6 h-6 mx-auto mb-2 text-orange-500" />
              <p className="text-xl font-bold">{stats.en_recepcion || 0}</p>
              <p className="text-xs text-slate-500">Recepción</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <Phone className="w-6 h-6 mx-auto mb-2 text-blue-500" />
              <p className="text-xl font-bold">{stats.llamados || 0}</p>
              <p className="text-xs text-slate-500">En Box</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
              <p className="text-xl font-bold">{stats.atendidos || 0}</p>
              <p className="text-xs text-slate-500">Atendidos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <XCircle className="w-6 h-6 mx-auto mb-2 text-red-500" />
              <p className="text-xl font-bold">{stats.no_show || 0}</p>
              <p className="text-xs text-slate-500">Ausentes</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Queue List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Turnos de Hoy</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setResetDialogOpen(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reiniciar
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead className="table-header sticky top-0">
                  <tr>
                    <th className="p-3 text-left">Turno</th>
                    <th className="p-3 text-center">Tipo</th>
                    <th className="p-3 text-center">Estado</th>
                    <th className="p-3 text-center">Box</th>
                    <th className="p-3 text-right">Hora</th>
                  </tr>
                </thead>
                <tbody>
                  {turnos.turnos?.slice().reverse().map((turno) => (
                    <tr key={turno.id} className="table-row">
                      <td className="p-3 font-mono font-bold">{turno.numero_formateado}</td>
                      <td className="p-3 text-center">
                        <Badge className={
                          turno.tipo_turno === 'EMPRESA' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-slate-100 text-slate-700'
                        }>
                          {turno.tipo_turno === 'EMPRESA' ? 'Empresa' : 'General'}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={
                          turno.status === 'ESPERANDO' ? 'bg-amber-100 text-amber-700' :
                          turno.status === 'EN_RECEPCION' ? 'bg-orange-100 text-orange-700' :
                          turno.status === 'LLAMADO' ? 'bg-blue-100 text-blue-700' :
                          turno.status === 'ATENDIDO' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {turno.status === 'EN_RECEPCION' ? 'Recepción' : turno.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        {turno.punto_atencion && turno.status === 'LLAMADO' ? (
                          <span className="text-sm font-medium">
                            Box {turno.punto_atencion}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-right text-sm text-slate-500">
                        {new Date(turno.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                  {(!turnos.turnos || turnos.turnos.length === 0) && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-slate-500">
                        No hay turnos registrados hoy
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Box Selection Dialog */}
        <Dialog open={boxSelectDialogOpen} onOpenChange={setBoxSelectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Seleccionar Box</DialogTitle>
              <DialogDescription>
                Elige el box para el turno {selectedTurnoForBox?.numero_formateado}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3 py-4">
              {puntosActivos.map((punto) => (
                <Button
                  key={punto.id}
                  variant="outline"
                  className="h-16 flex flex-col gap-1"
                  style={{ borderColor: punto.color }}
                  onClick={() => llamarBox(selectedTurnoForBox?.id, punto.numero)}
                  disabled={calling}
                >
                  <span className="font-semibold">{punto.nombre}</span>
                  <span className="text-xs text-slate-500">
                    {turnosPorBox[punto.numero] ? `Ocupado: ${turnosPorBox[punto.numero].numero_formateado}` : 'Disponible'}
                  </span>
                </Button>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBoxSelectDialogOpen(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Config Dialog */}
        <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Configuración de Turnos</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Habilitar Caja 2</Label>
                  <p className="text-sm text-slate-500">Activa un segundo punto de atención</p>
                </div>
                <Switch
                  checked={config?.segundo_punto_activo || false}
                  onCheckedChange={(checked) => updateConfig({ segundo_punto_activo: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setConfigDialogOpen(false)}>Cerrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Confirmation Dialog */}
        <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Reiniciar todos los turnos?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción reiniciará el contador de turnos y marcará todos los turnos actuales como archivados.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={reiniciarTurnos}
                className="bg-red-600 hover:bg-red-700"
              >
                Reiniciar Turnos
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default TurnosPage;
